import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActionLink,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  FormDateInput,
  FormInput,
} from "@prismicio/editor-ui"
import { useMemo, useState } from "react"

import { DeferredCodeBlock as CodeBlock } from "../../../../../DeferredCodeBlock"
import { prismicClientDocsUrl } from "../../../../constants"
import { useAPIExplorer } from "../../../APIExplorerContext"
import { apiPaths, RequestAPIPathFieldset } from "../RequestAPIPathFieldset"
import { FormMultiInput } from "../RequestFormMultiInput"
import { TagsFieldset } from "../RequestTagsFieldset"
import { formatFilterSnippet } from "./formatFilterSnippet"
import {
  type Filter,
  type FilterAnyArg,
  filters,
  type FilterType,
} from "./types"

interface FilterConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: FilterType
  editIndex?: number
}

export function FilterConfigDialog(props: FilterConfigDialogProps) {
  const { open, onOpenChange, type, editIndex } = props

  const { request } = useAPIExplorer()

  const isEdit = editIndex !== undefined

  const filter =
    editIndex !== undefined ? request.filters[editIndex] : undefined

  const [args, setArgs] = useState<(FilterAnyArg | undefined)[]>(
    filter ? filter.args : [],
  )

  function handleSubmit() {
    const newFilter = { type, args } as Filter

    if (isEdit) {
      // Update existing filter
      request.setFilters([
        ...request.filters.slice(0, editIndex),
        newFilter,
        ...request.filters.slice(editIndex + 1),
      ])
    } else {
      // Add new filter
      request.setFilters([...request.filters, newFilter])
    }
    onOpenChange(false)
  }

  const canSubmit = useMemo(() => {
    const expectedArgsLength = filters.find((f) => f.type === type)?.args.length
    // Since we want all args to be defined, we filter out undefined values
    const argsLength = args.filter((arg) => arg !== undefined).length

    return expectedArgsLength === argsLength
  }, [type, args])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="small">
      <DialogHeader title={`${isEdit ? "Edit" : "Add"} \`${type}\` filter`} />
      <DialogContent>
        <DialogDescription asChild>
          <Box display="grid" gap={16} padding={{ inline: 24, block: 16 }}>
            <FilterConfigFieldset type={type} args={args} setArgs={setArgs} />
            <CodeBlock
              language="typescript"
              code={formatFilterSnippet({ type, args } as Filter)}
              showLineNumbers={false}
            />
          </Box>
        </DialogDescription>
      </DialogContent>
      <DialogActions>
        <DialogActionLink
          color="purple11"
          href={`${prismicClientDocsUrl}#${type.toLowerCase()}`}
          underline
        >
          `{type}` filter documentation
        </DialogActionLink>
        <DialogCancelButton onClick={() => onOpenChange(false)} />
        <DialogActionButton onClick={handleSubmit} disabled={!canSubmit}>
          {isEdit ? "Update" : "Add"} filter
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

type FilterConfigFieldsetProps = {
  type: FilterType
  args: (FilterAnyArg | undefined)[]
  setArgs: (
    callback: (
      args: (FilterAnyArg | undefined)[],
    ) => (FilterAnyArg | undefined)[],
  ) => void
}

function FilterConfigFieldset(props: FilterConfigFieldsetProps) {
  const { type, args, setArgs } = props

  function updateArgs(index: number, value: FilterAnyArg | undefined) {
    setArgs((args) => {
      const newArgs = [...args]

      if (
        (typeof value === "number" && isNaN(value)) ||
        (typeof value === "string" && !value) ||
        (Array.isArray(value) && value.length === 0)
      ) {
        newArgs[index] = undefined
      } else {
        newArgs[index] = value
      }

      return newArgs
    })
  }

  function updatePath(path: string) {
    updateArgs(0, path)

    // Cast values to proper types according to the filter type and path value
    switch (type) {
      case "at":
      case "not":
        if (path === apiPaths.docTags) {
          updateArgs(
            1,
            typeof args[1] === "string"
              ? args[1].split(",").map((v) => v.trim())
              : [],
          )
        } else if (Array.isArray(args[1])) {
          updateArgs(
            1,
            args[1].map((v) => castFieldValue(v.toString(), path)).join(", "),
          )
        } else {
          updateArgs(1, castFieldValue(args[1]?.toString() || "", path))
        }
        break

      case "any":
        if (Array.isArray(args[1])) {
          updateArgs(
            1,
            args[1].map((v) => castFieldValue(v.toString(), path)),
          )
        }
        break

      default:
        break
    }
  }

  // Cast a string field value to a number, boolean, or string when appropriate
  const castFieldValue = (
    stringValue: string,
    path: string,
  ): string | number | boolean => {
    // Is the current path is not a `my.[document-type].[field]` path, we don't cast the value.
    if (
      typeof path !== "string" ||
      !path.startsWith("my.") ||
      path.endsWith(".uid")
    ) {
      return stringValue
    }

    const maybeNumber = parseFloat(stringValue)

    // We also check that the string representation of the number is the same as the original string
    // to allow users to enter floating point numbers and other values starting with numbers.
    if (!isNaN(maybeNumber) && maybeNumber.toString() === stringValue) {
      return maybeNumber
    } else if (stringValue === "true") {
      return true
    } else if (stringValue === "false") {
      return false
    } else {
      return stringValue
    }
  }

  switch (type) {
    case "at":
    case "not":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a field or document metadata property."
            allowedPaths={[
              apiPaths.docType,
              apiPaths.docTags,
              apiPaths.docId,
              apiPaths.myDocUid,
              apiPaths.myDocField,
            ]}
            context="filter"
          />
          {args[0] === "document.tags" ? (
            <TagsFieldset
              tags={
                Array.isArray(args[1]) ? args[1].map((s) => s.toString()) : []
              }
              setTags={(value) => updateArgs(1, value)}
              size="large"
              overflowWidth={84}
              label="Tags"
              description="Tags to compare against."
              error={!args[1]}
            />
          ) : (
            <FormInput
              value={args[1]?.toString() || ""}
              onValueChange={(value) =>
                updateArgs(1, castFieldValue(value, args[0]?.toString() || ""))
              }
              label="Value"
              description="The value to compare against."
              error={args[1] === undefined}
            />
          )}
        </>
      )

    case "any":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a field or document metadata property."
            allowedPaths={[
              apiPaths.docType,
              apiPaths.docTags,
              apiPaths.docId,
              apiPaths.myDocUid,
              apiPaths.myDocField,
            ]}
            context="filter"
          />
          {args[0] === "document.tags" ? (
            <TagsFieldset
              tags={
                Array.isArray(args[1]) ? args[1].map((v) => v.toString()) : []
              }
              setTags={(value) => updateArgs(1, value)}
              size="large"
              overflowWidth={84}
              label="Tags"
              description="Tags to compare against."
              error={!args[1]}
            />
          ) : (
            <FormMultiInput
              value={
                Array.isArray(args[1]) ? args[1].map((v) => v.toString()) : []
              }
              onValueChange={(value) =>
                updateArgs(
                  1,
                  value.map((v) =>
                    castFieldValue(v, args[0]?.toString() || ""),
                  ),
                )
              }
              label="Values"
              placeholder="Comma-separated values"
              description="Values to compare against."
              error={args[1] === undefined}
            />
          )}
        </>
      )

    case "in":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to an ID or UID property."
            allowedPaths={[apiPaths.docId, apiPaths.myDocUid]}
            context="filter"
          />
          <FormMultiInput
            value={
              Array.isArray(args[1]) ? args[1].map((s) => s.toString()) : []
            }
            onValueChange={(value) => updateArgs(1, value)}
            label="Values"
            placeholder="Comma-separated values"
            description="IDs or UIDs to compare against."
            error={!args[1]}
          />
        </>
      )

    case "fulltext":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a field or `document` to search the whole document."
            allowedPaths={[apiPaths.doc, apiPaths.myDocField]}
            context="filter"
          />
          <FormInput
            value={args[1]?.toString() || ""}
            onValueChange={(value) => updateArgs(1, value)}
            label="Search Term"
            description="The search term to find."
            error={!args[1]}
          />
        </>
      )

    case "has":
    case "missing":
      return (
        <RequestAPIPathFieldset
          path={args[0]?.toString() || ""}
          setPath={updatePath}
          description="A path to a field."
          allowedPaths={[apiPaths.myDocField]}
          context="filter"
        />
      )

    case "geopointNear":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a geopoint field."
            allowedPaths={[apiPaths.myDocField]}
            context="filter"
          />
          <FormInput
            type="number"
            value={args[1]?.toString() || ""}
            onValueChange={(value) => updateArgs(1, parseFloat(value))}
            label="Latitude"
            description="The latitude coordinate."
            error={args[1] === undefined}
          />
          <FormInput
            type="number"
            value={args[2]?.toString() || ""}
            onValueChange={(value) => updateArgs(2, parseFloat(value))}
            label="Longitude"
            description="The longitude coordinate."
            error={args[2] === undefined}
          />
          <FormInput
            type="number"
            value={args[3]?.toString() || ""}
            onValueChange={(value) => updateArgs(3, parseFloat(value))}
            label="Radius (km)"
            description="The search radius in kilometers."
            error={args[3] === undefined}
          />
        </>
      )

    case "numberLessThan":
    case "numberGreaterThan":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a number field."
            allowedPaths={[apiPaths.myDocField]}
            context="filter"
          />
          <FormInput
            type="number"
            value={args[1]?.toString() || ""}
            onValueChange={(value) => updateArgs(1, parseFloat(value))}
            label="Number"
            description="The number to compare."
            error={args[1] === undefined}
          />
        </>
      )

    case "numberInRange":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a number field."
            allowedPaths={[apiPaths.myDocField]}
            context="filter"
          />
          <FormInput
            type="number"
            value={args[1]?.toString() || ""}
            onValueChange={(value) => updateArgs(1, parseFloat(value))}
            label="Lower limit"
            description="The lower limit of the range."
            error={args[1] === undefined}
          />
          <FormInput
            type="number"
            value={args[2]?.toString() || ""}
            onValueChange={(value) => updateArgs(2, parseFloat(value))}
            label="Upper limit"
            description="The upper limit of the range."
            error={args[2] === undefined}
          />
        </>
      )

    case "dateAfter":
    case "dateBefore":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a field or document metadata property."
            allowedPaths={[
              apiPaths.docFirstPublicationDate,
              apiPaths.docLastPublicationDate,
              apiPaths.myDocField,
            ]}
            context="filter"
          />
          <FormDateInput
            value={args[1] ? new Date(args[1].toString()) : undefined}
            onValueChange={(date) =>
              updateArgs(1, date?.toISOString().split("T")[0])
            }
            label="Date"
            description="The date to compare."
            error={!args[1]}
            mode="date"
          />
        </>
      )

    case "dateBetween":
      return (
        <>
          <RequestAPIPathFieldset
            path={args[0]?.toString() || ""}
            setPath={updatePath}
            description="A path to a field or document metadata property."
            allowedPaths={[
              apiPaths.docFirstPublicationDate,
              apiPaths.docLastPublicationDate,
              apiPaths.myDocField,
            ]}
            context="filter"
          />
          <FormDateInput
            value={args[1] ? new Date(args[1].toString()) : undefined}
            onValueChange={(date) =>
              updateArgs(1, date?.toISOString().split("T")[0])
            }
            label="Start date"
            description="The start date to compare."
            error={!args[1]}
            mode="date"
          />
          <FormDateInput
            value={args[2] ? new Date(args[2].toString()) : undefined}
            onValueChange={(date) =>
              updateArgs(2, date?.toISOString().split("T")[0])
            }
            label="End date"
            description="The end date to compare."
            error={!args[2]}
            mode="date"
          />
        </>
      )

    default:
      return null
  }
}
